/*********************************************************************************************
 * ⚙️ CONFIGURATION & CONSTANTS
 */
const CART_CONFIG = {
	// API Settings
	debounceDelay: 500,
	apiTimeout: 10000,
	
	// UI Settings
	carousel: {
		type: 'slide',
		arrows: false,
		pagination: false,
		rewind: true,
		gap: 12,
		perPage: 1
	},
	
	// Selectors
	selectors: {
		cart: '[data-cart], .mm-cart',
		cartWrapper: '[data-cart-wrapper]',
		cartProducts: '[data-cart-products]',
		cartBubbles: '[data-cart-count-bubble]',
		addToCartForm: '[data-type="add-to-cart-form"]',
		cartClose: '[data-cart-close]',
		checkoutBtn: '[data-checkout-btn]',
		drawerCart: '[data-drawer-cart]',
		cartItemById: '[data-cart-item-id="{{id}}"]',
		lineIndex: '[data-line-index="{{index}}"]',
		itemQuantity: '[data-item-quantity]',
		freeProducts: '[data-free-products]',
		splideRea: '.splide-rea',
		splideCross: '.splide-cross'
	},
	
	// Development mode (uses global isDevelopment from scripts.liquid)
	isDevelopment: typeof isDevelopment !== 'undefined' ? isDevelopment : false
};

/*********************************************************************************************
 * Initialize the Splide carousel for the cart section.
 *
 */
function mmInitSplideRea() {
	const id = ".splide-rea";
	const splideElem = document.querySelector(id);

	if (splideElem) {
		new Splide(splideElem, {
			type: "splide",
			autoplay: true,
			arrows: false,
			pagination: false,
			rewind: true,
			gap: 12,
			perPage: 1,
		}).mount();
	}
}

function mmInitSplideCross() {
	const id = ".splide-cross";
	const splideElem = document.querySelector(id);

	if (splideElem) {
		new Splide(splideElem, {
			type: "splide",
			arrows: false,
			pagination: false,
			rewind: true,
			gap: 12,
			perPage: 1,
		}).mount();
	}
}

/*********************************************************************************************
 * Add a product with upsell to the cart.
 *
 * @param {number} parentVariantId - The ID of the parent variant.
 * @param {number} upsellVariantId - The ID of the upsell variant.
 * @param {Object} options - The options for adding the product.
 * @param {number} [options.parent_quantity=1] - The quantity of the parent product.
 * @param {boolean} [options.openCart=true] - Whether to open the cart drawer after adding the product.
 * @returns {Promise<Object>} - A promise resolving to the response data.
 */
function mmAddProductWithUpsellToCart(parentVariantId, upsellVariantId, { parent_quantity = 1, openCart = true }) {
	mmAPIRequest("POST", "cart/add.js", {
		items: [
			{
				id: parentVariantId,
				quantity: parent_quantity,
			},
			{
				id: upsellVariantId,
				quantity: 1,
				properties: {
					_parent_product: parentVariantId,
				},
			},
		],
		sections: mmCartSectionId(),
	})
		.then(refreshCartFromData)
		.then((data) => {
			if (openCart) {
				setTimeout(mmOpenCart);
			}
		})
		.catch((error) => {
			try { document.dispatchEvent(new CustomEvent('cart:error', { detail: { source: 'add_with_upsell', error } })); } catch (_) {}
			// Rafraîchir le panier pour refléter l'état réel sans recharger la page
			try { mmOpenAndRefreshCart(); } catch (_) { try { mmOpenCart(); } catch (_) {} }
		});
}

/*********************************************************************************************
 * Add a product to the cart.
 *
 * @param {number} variantId - The ID of the variant to add.
 * @param {Object} options - The options for adding the product.
 * @param {number} [options.quantity=1] - The quantity to add.
 * @param {boolean} [options.openCart=true] - Whether to open the cart drawer after adding the product.
 * @returns {Promise<Object>} - A promise resolving to the response data.
 */
function mmAddProductToCart(variantId, { quantity = 1, openCart = false, sourceElement = null }) {
	// Préparer la requête avec sections si disponible
	const requestBody = {
		id: variantId,
		quantity: quantity,
	};

	// Cap côté UI: empêcher plus que le stock max si on a la carte source
	try {
		if (sourceElement) {
			const productCardEl = sourceElement.closest('.mm-product-card');
			if (productCardEl) {
				const maxQty = parseInt(productCardEl.dataset.maxQuantity, 10);
				if (!isNaN(maxQty) && maxQty > 0) {
					// Quantité actuelle en panier pour ce variant si visible dans la carte
					let currentQtyInCart = 0;
					const qtyEl = productCardEl.querySelector('.mm-product-card-cart-quantity');
					if (qtyEl) {
						currentQtyInCart = parseInt(qtyEl.textContent) || 0;
					}
					const capped = Math.max(0, Math.min(quantity, Math.max(0, maxQty - currentQtyInCart)));
					if (capped === 0) {
						// Rien à ajouter, on sort proprement
						return Promise.resolve({ status: 200, item_count: undefined });
					}
					requestBody.quantity = capped;
				}
			}
		}
	} catch (_) {}
	
	// Demander aussi le rendu de la cart bar dans la même requête: garantit un HTML à jour,
	// sans latence/risque de cache d'un fetch séparé.
	const sectionId = mmCartSectionId();
	requestBody.sections = [sectionId, 'mm-cart-bar'].filter(Boolean).join(',');

	return mmAPIRequest("POST", "cart/add.js", requestBody)
		.then((data) => {
			try {
				const cartBarHtml = data.sections && data.sections['mm-cart-bar'];
				document.dispatchEvent(new CustomEvent('cartbar:add', { detail: { variantId, image: data.image, cartBarHtml } }));
			} catch (_) {}

			// 1) TOUJOURS mettre à jour le compteur du panier en premier
			let cartCountPromise;
			if (data.item_count !== undefined) {
				updateCartCount(data.item_count);
				cartCountPromise = Promise.resolve(data);
			} else {
				// Récupérer manuellement le nombre total d'articles dans le panier
				cartCountPromise = mmGetCart().then(cartData => {
					if (cartData.item_count !== undefined) {
						updateCartCount(cartData.item_count);
					}
					return data; // Retourner les données originales pour la suite
				});
			}
			
			// Continuer avec le reste de la logique après la mise à jour du compteur
			return cartCountPromise.then((finalData) => {
				// 2) Rafraîchir la carte de produit source si possible
				if (sourceElement && typeof window.refreshProductCards === 'function') {
					const productCardEl = sourceElement.closest('.mm-product-card');
					if (productCardEl) {
						const pid = parseInt(productCardEl.dataset.productId, 10) || finalData.product_id;
						window.refreshProductCards(pid, productCardEl);
					}
				}
				
				// 3) Forcer la mise à jour des états CTA/cart-state immédiate
				if (typeof window.mmProductCardsOnCartUpdated === 'function') {
					window.mmProductCardsOnCartUpdated();
				}
				
				// 4) Mettre à jour l'indicateur de quantité sur la page produit principal
				// Vérifier si on est sur une page produit (pas seulement si sourceElement existe)
				const isProductPage = document.querySelector('.mm-main-product-medias');
				if (isProductPage) {
					// Récupérer l'ID du produit depuis le formulaire
					const productId = document.querySelector('#product_id')?.value || finalData.product_id;
					if (productId) {
						// Récupérer la quantité totale du produit dans le panier
						mmGetCart().then(cartData => {
							if (cartData && cartData.items) {
								let totalQuantity = 0;
								cartData.items.forEach(item => {
									if (item.product_id == productId) {
										totalQuantity += item.quantity;
									}
								});
								updateMainProductCartIndicator(productId, totalQuantity);
							}
						});
					}
				}
				
				// 5) Si on a des sections, rafraîchir le panier complet
				if (sectionId && finalData.sections && finalData.sections[sectionId]) {
					return refreshCartFromData(finalData);
				} else {
					// Sinon, essayer de récupérer le HTML du panier via section_id
					if (sectionId) {
						return fetch(`${window.Shopify.routes.root}?section_id=${sectionId}`)
							.then(res => res.text())
							.then((html) => {
								const tempData = { sections: {} };
								tempData.sections[sectionId] = html;
								return refreshCartFromData(tempData);
							})
							.catch((error) => {
								// Fallback: juste déclencher l'événement (updateCartCount déjà fait)
								document.dispatchEvent(new CustomEvent('cart:updated'));
								return finalData;
							});
					} else {
						// Pas de section panier dans le DOM, fallback léger: juste l'événement (updateCartCount déjà fait)
						document.dispatchEvent(new CustomEvent('cart:updated'));
						return finalData;
					}
				}
			});
		})
		.then((data) => {
			// Afficher le toaster après ajout réussi
			if (typeof mmShowCartToaster === 'function') {
				mmShowCartToaster();
			}
			
			if (openCart) {
				setTimeout(mmOpenCart);
			}
			return data;
		})
		.catch((error) => {
			try { document.dispatchEvent(new CustomEvent('cart:error', { detail: { source: 'add', error } })); } catch (_) {}
			// Rafraîchir le panier pour refléter l'état réel sans recharger la page
			try { mmOpenAndRefreshCart(); } catch (_) { try { mmOpenCart(); } catch (_) {} }
		});
}

/*********************************************************************************************
 * Update product quantity from product card buttons (increase/decrease).
 * This function works like mmAddProductToCart but handles quantity changes
 * for products already in cart, with proper card refresh and cart count update.
 *
 * @param {number} lineIndex - The line index in the cart (not the variant ID).
 * @param {number} currentQuantity - The current quantity in cart.
 * @param {number} quantityChange - The quantity change (positive for increase, negative for decrease).
 * @param {HTMLElement} sourceElement - The button element that was clicked.
 * @returns {Promise<Object>} - A promise resolving to the response data.
 */
function mmUpdateProductQuantityInCard(lineIndex, currentQuantity, quantityChange, sourceElement) {
	if (quantityChange > 0) {
		try {
			// Prefer the product card's own image (always present) over the cart drawer's line
			// item DOM, which may not be mounted/up to date when adding from a collection page.
			const cardImage = sourceElement?.closest('.mm-product-card')?.querySelector('.mm-product-card-image-container img')?.src;
			const cartItemEl = mmCartItem(lineIndex);
			const variantId = cartItemEl?.dataset.cartItemId;
			const image = cardImage || cartItemEl?.querySelector('.mm-dc-product-image img')?.src;
			document.dispatchEvent(new CustomEvent('cartbar:add', { detail: { variantId, image } }));
		} catch (_) {}
	}

	// Calculate new quantity
	let newQuantity = currentQuantity + quantityChange;

	// Lire le max depuis la ligne du panier si présent
	try {
		const itemCart = mmCartItem(lineIndex);
		if (itemCart) {
			const maxQtty = parseInt(itemCart.dataset.maxQuantity, 10);
			if (!isNaN(maxQtty) && maxQtty > 0 && newQuantity > maxQtty) {
				newQuantity = maxQtty;
			}
		}
	} catch (_) {}
	
	// Prevent negative quantities
	if (newQuantity <= 0) {
		// Remove product from cart
		return mmAPIRequest("POST", "cart/change.js", {
			line: lineIndex,
			quantity: 0,
			sections: mmCartSectionId()
		})
		.then((data) => {
			// Update cart count
			if (data.item_count !== undefined) {
				updateCartCount(data.item_count);
			} else {
				// Fallback si item_count manquant
				mmGetCart().then(cartData => {
					if (cartData && typeof cartData.item_count !== 'undefined') {
						updateCartCount(cartData.item_count);
					}
				});
			}
			
			// Refresh product cards to show add to cart button
			if (sourceElement && typeof window.refreshProductCards === 'function') {
				const productCardEl = sourceElement.closest('.mm-product-card');
				if (productCardEl) {
					const pid = parseInt(productCardEl.dataset.productId, 10);
					window.refreshProductCards(pid, productCardEl);
				}
			}
			
			// Force CTA/cart-state update
			if (typeof window.mmProductCardsOnCartUpdated === 'function') {
				window.mmProductCardsOnCartUpdated();
			}
			
			// Mettre à jour l'indicateur de quantité sur la page produit principal (masquer)
			if (sourceElement) {
				const productCardEl = sourceElement.closest('.mm-product-card');
				if (productCardEl) {
					const productId = parseInt(productCardEl.dataset.productId, 10);
					updateMainProductCartIndicator(productId, 0);
				}
			}
			
			// Refresh cart if sections available
			if (data.sections && mmCartSectionId()) {
				return refreshCartFromData(data);
			} else {
				document.dispatchEvent(new CustomEvent('cart:updated'));
				return data;
			}
		})
		.catch((error) => {
			try { document.dispatchEvent(new CustomEvent('cart:error', { detail: { source: 'change-remove', error } })); } catch (_) {}
			try { mmOpenAndRefreshCart(); } catch (_) { try { mmOpenCart(); } catch (_) {} }
		});
	}
	
	// Update quantity in cart
	return mmAPIRequest("POST", "cart/change.js", {
		line: lineIndex,
		quantity: newQuantity,
		sections: mmCartSectionId()
	})
	.then((data) => {
		// Update cart count
		if (data.item_count !== undefined) {
			updateCartCount(data.item_count);
		} else {
			// Fallback si item_count manquant
			mmGetCart().then(cartData => {
				if (cartData && typeof cartData.item_count !== 'undefined') {
					updateCartCount(cartData.item_count);
				}
			});
		}
		
					// Refresh product cards to show updated quantity
			if (sourceElement && typeof window.refreshProductCards === 'function') {
				const productCardEl = sourceElement.closest('.mm-product-card');
				if (productCardEl) {
					const pid = parseInt(productCardEl.dataset.productId, 10);
					window.refreshProductCards(pid, productCardEl);
				}
			}
		
		// Force CTA/cart-state update
		if (typeof window.mmProductCardsOnCartUpdated === 'function') {
			window.mmProductCardsOnCartUpdated();
		}
		
		// Mettre à jour l'indicateur de quantité sur la page produit principal
		const isProductPage = document.querySelector('.mm-main-product-medias');
		if (isProductPage) {
			// Récupérer l'ID du produit depuis le formulaire
			const productId = document.querySelector('#product_id')?.value;
			if (productId) {
				// Récupérer la quantité totale du produit dans le panier
				mmGetCart().then(cartData => {
					if (cartData && cartData.items) {
						let totalQuantity = 0;
						cartData.items.forEach(item => {
							if (item.product_id == productId) {
								totalQuantity += item.quantity;
							}
						});
						updateMainProductCartIndicator(productId, totalQuantity);
					}
				});
			}
		} else if (sourceElement) {
			// Logique pour les cartes de produit dans les grilles
			const productCardEl = sourceElement.closest('.mm-product-card');
			if (productCardEl) {
				const productId = parseInt(productCardEl.dataset.productId, 10);
				// Récupérer la quantité totale du produit dans le panier
				mmGetCart().then(cartData => {
					if (cartData && cartData.items) {
						let totalQuantity = 0;
						cartData.items.forEach(item => {
							if (item.product_id === productId) {
								totalQuantity += item.quantity;
							}
						});
						updateMainProductCartIndicator(productId, totalQuantity);
					}
				});
			}
		}
		
		// Refresh cart if sections available
		if (data.sections && mmCartSectionId()) {
			return refreshCartFromData(data);
		} else {
			document.dispatchEvent(new CustomEvent('cart:updated'));
			return data;
		}
	})
	.catch((error) => {
		try { document.dispatchEvent(new CustomEvent('cart:error', { detail: { source: 'change-update', error } })); } catch (_) {}
		try { mmOpenAndRefreshCart(); } catch (_) { try { mmOpenCart(); } catch (_) {} }
	});
}

/*********************************************************************************************
 * Add a product to the cart from the product form.
 *
 * @returns {void}
 */
function mmAddProductToCartFromProductFrom() {
	// 1: on regarde si la checkbox de cross-sell existe, si oui on regarde si elle est checked

	const upsellMetafieldElement = document.getElementById("upsell-metafield");

	// Si l'élément existe, vérifier le dataset
	if (upsellMetafieldElement && upsellMetafieldElement.dataset.upsell) {
		// Récupérer la valeur du metafield upsell
		const upsellMetafield = upsellMetafieldElement.dataset.upsell;

		// Logique d'ajout au panier
		const checkbox = document.querySelector(".mm-product-form .mm-check-upsell");
		const variant_id = document.getElementById("product_id").value;
		const variant_quantity = document.querySelector('input[name="quantity"]');

		if (checkbox) {
			if (checkbox.checked) {
				const upsell_variant_id = checkbox.dataset.variantId;
				mmAddProductWithUpsellToCart(variant_id, upsell_variant_id, { parent_quantity: variant_quantity ? parseInt(variant_quantity.value) : 1, openCart: false });
			} else {
				mmAddProductToCart(variant_id, { quantity: variant_quantity ? parseInt(variant_quantity.value) : 1, openCart: false });
			}
		}
	} else {
		const checkbox = document.querySelector(".mm-product-form .mm-check-upsell");
		const variant_id = document.getElementById("product_id").value;
		const variant_quantity = document.querySelector('input[name="quantity"]');

		mmAddProductToCart(variant_id, { quantity: variant_quantity ? parseInt(variant_quantity.value) : 1, openCart: false });
	}
}

/*********************************************************************************************
 * Creates a debounced function that delays invoking func until after duration milliseconds have elapsed since the last time the debounced function was invoked.
 *
 * @param {Function} func - The function to debounce.
 * @param {number} duration - The number of milliseconds to delay.
 * @returns {Function} - The debounced function.
 */
function debounce(func, duration) {
	let timeout;
	return function (...args) {
		const effect = () => {
			timeout = null;
			return func.apply(this, args);
		};
		clearTimeout(timeout);
		timeout = setTimeout(effect, duration);
	};
}

/*********************************************************************************************
 * Get the full cart section ID from the DOM.
 *
 * @returns {HTMLElement} - The full cart section ID with the prefix.
 */
function mmCartSectionIdLong() {
	const el = document.querySelector(".mm-cart");
	return el && el.id ? el.id : null;
}

/*********************************************************************************************
 * Get the cart section ID from the DOM, removing the "shopify-section-" prefix.
 *
 * @returns {string} - The cart section ID without the "shopify-section-" prefix.
 */
function mmCartSectionId() {
	const cartElement = document.querySelector(".mm-cart");
	if (!cartElement || !cartElement.id) {
		// Pas de section panier dans le DOM (page sans drawer/cart)
		return null;
	}
	return cartElement.id.replace("shopify-section-", "");
}

/*********************************************************************************************
 * Get the cart section element from the DOM.
 *
 * @returns {HTMLElement} - The cart section element.
 */
function mmCartSectionElement() {
	const id = mmCartSectionIdLong();
	if (!id) return null;
	return document.getElementById(`${id}`);
}

/*********************************************************************************************
 * Get the cart product container element from the cart section.
 *
 * @returns {HTMLElement} - The cart product container element.
 */
function mmCartProductsContainerElement() {
	const section = mmCartSectionElement();
	if (!section) return null;
	return section.querySelector(".mm-dc-products-container");
}

/*********************************************************************************************
 * Get the cart item element for a specific product variant.
 *
 * @param {number} line_index - The line item index.
 * @returns {HTMLElement} - The cart item element.
 */
function mmCartItem(line_index) {
	const container = mmCartProductsContainerElement();
	if (!container) return null;
	return container.querySelector(`[data-line-index="${line_index}"]`);
}

/*********************************************************************************************
 * Get the cart item quantity element for a specific product variant.
 *
 * @param {number} line_index - The line item index.
 * @returns {HTMLElement} - The cart item quantity element.
 */
function mmCartItemQuantityEl(line_index) {
	const itemEl = mmCartItem(line_index);
	if (!itemEl) return null;
	return itemEl.querySelector("[data-item-quantity]");
}

/*********************************************************************************************
 * Get the quantity of a specific cart item.
 *
 * @param {number} line_index - The line item index.
 * @returns {number} - The quantity of the cart item.
 */
function mmGetCartItemQuantity(line_index) {
	const quantityEl = mmCartItemQuantityEl(line_index);
	if (!quantityEl) return null;
	return Number.parseInt(quantityEl.dataset.itemQuantity);
}

/*********************************************************************************************
 * Set the quantity of a specific cart item and update the DOM.
 *
 * @param {number} line_index - The line item index.
 * @param {number} newQuantity - The new quantity to set.
 */
function mmSetCartItemQuantity(line_index, newQuantity) {
	// Display quantity in front
	if (newQuantity >= 0) {
		const quantityEl = mmCartItemQuantityEl(line_index);
		// Vérifier si l'élément existe (peut être null pour les produits avec enfants)
		if (quantityEl) {
			quantityEl.dataset.itemQuantity = newQuantity;
			quantityEl.innerText = newQuantity;
		}
	}
}

/*********************************************************************************************
 * Create a new cart element from the provided data.
 *
 * @param {Object} data - The data containing cart HTML.
 * @returns {HTMLElement} - The new cart element.
 */
function mmCartHTMLFromData(data) {
	const sectionId = mmCartSectionId();
	if (!sectionId || !data.sections || !data.sections[sectionId]) return null;
	const el = document.createElement("DIV");
	el.innerHTML = data.sections[sectionId];
	return el;
}

/*********************************************************************************************
 * Extract the items container from the new cart HTML.
 *
 * @param {Object} data - The data containing cart HTML.
 * @returns {HTMLElement} - The items container element.
 */
function extractItemsFromCartHTML(data) {
	const el = mmCartHTMLFromData(data);
	if (!el) return null;
	return el.querySelector(".mm-dc-products-container");
}

/*********************************************************************************************
 * Insert cart products from the response data into the current cart in the DOM.
 *
 * @param {Object} data - The data containing the new cart HTML.
 * @returns {Object} - The response data.
 */
function insertCartProductsFromResponse(data) {
	const itemsContainer = extractItemsFromCartHTML(data);
	const container = mmCartProductsContainerElement();
	if (!itemsContainer || !container) return data;
	container.innerHTML = itemsContainer.innerHTML;
	return data;
}

/*********************************************************************************************
 * Extract the total price element from the new cart HTML.
 *
 * @param {Object} data - The data containing the new cart HTML.
 * @returns {HTMLElement} - The total price element.
 */
function extractTotalPriceFromCartHTML(data) {
	const cartEl = mmCartHTMLFromData(data);
	if (!cartEl) return null;
	return cartEl.querySelector("[data-cart-total-price]");
}

/*********************************************************************************************
 * Insert the total price from the response data into the current cart in the DOM.
 *
 * @param {Object} data - The data containing the new cart HTML.
 * @returns {Object} - The response data.
 */
function insertInsertTotalPriceFromResponse(data) {
	const newTotalPriceEl = extractTotalPriceFromCartHTML(data);
	const section = mmCartSectionElement();
	if (!newTotalPriceEl || !section) return data;
	const currentTotalPriceEl = section.querySelector("[data-cart-total-price]");
	if (currentTotalPriceEl) currentTotalPriceEl.innerHTML = newTotalPriceEl.innerHTML;
	return data;
}

/*********************************************************************************************
 * Perform an API request to fetch or update cart data.
 *
 * @param {string} method - The HTTP method (GET, POST, etc.).
 * @param {string} path - The API endpoint path.
 * @param {Object} body - The request body data.
 * @returns {Promise<Object>} - A promise resolving to the response data.
 */
function mmAPIRequest(method, path, body) {
	const url = window.Shopify.routes.root + path;
	
	document.querySelector(".mm-dc-products")?.classList?.add("mm-inactive");
	
	const requestOptions = {
		method: method,
		headers: { "Content-Type": "application/json" }
	};
	
	// Only add body for non-GET requests
	if (method !== "GET" && body) {
		requestOptions.body = JSON.stringify(body);
	}
	
	return fetch(url, requestOptions)
		.then((response) => response.json())
		.then((data) => {
			document.querySelector(".mm-dc-products")?.classList?.remove("mm-inactive");
			// Ne pas mettre à jour le compteur automatiquement ici - chaque fonction appelante doit gérer sa propre mise à jour
			// Cela évite d'écraser des valeurs correctes avec des valeurs incorrectes (comme 0 si item_count est absent)
			if (data.status && data.status > 400) {
				throw data;
			} else {
				return data;
			}
		})
		.catch((error) => {
			throw error;
		});
}

/*********************************************************************************************
 * Fetch the current cart data.
 *
 * @param {Object} options - Optional parameters for the request.
 * @param {string} options.sections - Comma-separated list of section IDs to include.
 * @returns {Promise<Object>} - A promise resolving to the cart data.
 */
function mmGetCart(options = {}) {
	let path = "cart.js";
	
	// Add sections parameter to URL if provided
	if (options.sections) {
		path += `?sections=${options.sections}`;
	}
	
	return mmAPIRequest("GET", path)
		.then((data) => {
			// S'assurer que le compteur est toujours mis à jour, même si item_count n'est pas dans la réponse
			if (data && typeof data.item_count !== 'undefined') {
				updateCartCount(data.item_count);
			} else if (data && data.items) {
				// Calculer item_count depuis les items si non présent
				const calculatedCount = data.items.reduce((sum, item) => sum + item.quantity, 0);
				updateCartCount(calculatedCount);
			}
			
			return data;
		});
}

/*********************************************************************************************
 * Open the cart drawer and fetch the latest cart data.
 */
function mmOpenCart() {
	mmGetCart();
	const cartFilter = document.querySelector(".mm-drawer-cart-wrapper");
	const html = document.querySelector("html");

	if (!cartFilter || !html) return;

	cartFilter.classList.add("mm-dc-is-open");
	html.classList.add("stop-scrolling");

	// Initialize cross-sell carousel if needed
	mmInitSplideCross();

	// Hide third-party elements if they exist
	const shopifyChat = document.getElementById("shopify-chat");
	const axeptioOverlay = document.getElementById("axeptio_overlay");
	
	if (shopifyChat) {
		shopifyChat.style.display = "none";
	}
	if (axeptioOverlay) {
		axeptioOverlay.style.display = "none";
	}
}

/*********************************************************************************************
 * Close the cart drawer and allow page scrolling.
 *
 */
// function mmCloseCart() {
// 	document.querySelector(".mm-drawer-cart-wrapper").classList.remove("mm-dc-is-open");
// 	document.querySelector("html").classList.remove("stop-scrolling");
// }

/*********************************************************************************************
 * Close the cart page on mobile and redirect to the previous page or the home page.
 */

function mmCloseCart() {
	// Synchroniser le compteur avant de quitter la page pour s'assurer qu'il est à jour
	// Attendre que la synchronisation soit terminée avant de naviguer
	mmGetCart().then(cartData => {
		if (cartData && typeof cartData.item_count !== 'undefined') {
			updateCartCount(cartData.item_count);
		} else if (cartData && cartData.items) {
			// Calculer item_count depuis les items si non présent
			const calculatedCount = cartData.items.reduce((sum, item) => sum + item.quantity, 0);
			updateCartCount(calculatedCount);
		}
		
		// Une fois le compteur mis à jour, naviguer
		// Vérifie si l'utilisateur a une page précédente dans l'historique
		if (window.history.length > 1) {
			window.history.back();
		} else {
		// Si aucune page précédente n'existe (ex. : accès direct au panier)
		// on le redirige vers la page d'accueil
			window.location.href = '/';
		}
	}).catch(error => {
		// En cas d'erreur, naviguer quand même
		if (window.history.length > 1) {
			window.history.back();
		} else {
			window.location.href = '/';
		}
	});
}

/*********************************************************************************************
 * Re-render the cart inner HTML from the response data and initialize Splide carousels.
 *
 * @param {Object} data - The data containing the new cart HTML.
 * @returns {Object} - The response data.
 */
function refreshCartFromData(data) {
	let sectionId = mmCartSectionId();
	
	// Si pas de sectionId dans le DOM mais qu'on a des sections dans la réponse, extraire l'ID depuis les clés
	if (!sectionId && data && data.sections && Object.keys(data.sections).length > 0) {
		const sectionsKeys = Object.keys(data.sections).filter(key => 
			key && key !== 'null' && key !== 'undefined' && typeof key === 'string' && key.length > 0
		);
		
		if (sectionsKeys.length > 0) {
			// Chercher une section qui ressemble à une section panier
			const possibleSectionId = sectionsKeys.find(key => 
				key.includes('mm_cart') || key.includes('cart') || key.includes('mm-cart')
			);
			if (possibleSectionId) {
				sectionId = possibleSectionId;
			} else {
				// Prendre la première section valide disponible
				sectionId = sectionsKeys[0];
			}
		}
	}
	
	// Extraire les items du panier pour les logs
	let itemsBefore = [];
	try {
		const currentCart = mmCartProductsContainerElement();
		if (currentCart) {
			itemsBefore = Array.from(currentCart.querySelectorAll('[data-line-index]')).map(item => ({
				line_index: item.dataset.lineIndex,
				variant_id: item.dataset.cartItemId,
				title: item.querySelector('.mm-dc-product-title')?.textContent?.trim() || 'unknown'
			}));
		}
	} catch (e) {}
	
	// Check if data.sections exists and contains the cart section
	if (!sectionId || !data.sections || !data.sections[sectionId]) {
		// Just update cart count and dispatch event
		if (data && typeof data.item_count !== 'undefined') {
			updateCartCount(data.item_count);
		} else if (data && data.items) {
			// Calculer item_count depuis les items si non présent
			const calculatedCount = data.items.reduce((sum, item) => sum + item.quantity, 0);
			updateCartCount(calculatedCount);
		} else {
			// Fallback: récupérer le panier pour obtenir le bon item_count
			mmGetCart().then(cartData => {
				if (cartData && typeof cartData.item_count !== 'undefined') {
					updateCartCount(cartData.item_count);
				}
			});
		}
		document.dispatchEvent(new CustomEvent('cart:updated'));
		return data;
	}

	const newCartEl = document.createElement("DIV");
	newCartEl.innerHTML = data.sections[sectionId];
	
	// Si la section n'est pas dans le DOM, on ne peut pas la mettre à jour
	// Mais on peut quand même mettre à jour le compteur
	const sectionEl = mmCartSectionElement();
	const innerCart = sectionEl ? sectionEl.querySelector(".mm-drawer-cart") : null;
	
	if (!sectionEl && data.sections && data.sections[sectionId]) {
		if (data && typeof data.item_count !== 'undefined') {
			updateCartCount(data.item_count);
		} else if (data && data.items) {
			// Calculer item_count depuis les items si non présent
			const calculatedCount = data.items.reduce((sum, item) => sum + item.quantity, 0);
			updateCartCount(calculatedCount);
		} else {
			// Fallback: récupérer le panier pour obtenir le bon item_count
			mmGetCart().then(cartData => {
				if (cartData && typeof cartData.item_count !== 'undefined') {
					updateCartCount(cartData.item_count);
				}
			});
		}
		document.dispatchEvent(new CustomEvent('cart:updated'));
		return data;
	}
	
	// Extraire les items après le refresh
	let itemsAfter = [];
	try {
		const newCartContainer = newCartEl.querySelector('.mm-dc-products-container');
		if (newCartContainer) {
			itemsAfter = Array.from(newCartContainer.querySelectorAll('[data-line-index]')).map(item => ({
				line_index: item.dataset.lineIndex,
				variant_id: item.dataset.cartItemId,
				title: item.querySelector('.mm-dc-product-title')?.textContent?.trim() || 'unknown'
			}));
		}
	} catch (e) {}
	
	
	// Mémoriser les containers dont le détail des enfants est actuellement ouvert,
	// pour restaurer cet état après le re-render (sinon le wrapper repasse toujours
	// en visually-hidden car c'est l'état par défaut du markup serveur).
	let openChildrenContainerIds = [];
	if (innerCart) {
		openChildrenContainerIds = Array.from(innerCart.querySelectorAll('.mm-dc-product-children-wrapper:not(.visually-hidden)'))
			.map(wrapper => wrapper.closest('.mm-dc-product-container')?.dataset.cartItemId)
			.filter(Boolean);
	}

	if (innerCart && newCartEl.querySelector(".mm-drawer-cart")) {
		innerCart.innerHTML = newCartEl.querySelector(".mm-drawer-cart").innerHTML;
		mmInitSplideRea();
		mmInitSplideCross();
		// Réinitialiser les dropdowns (dont le code promo) après re-render
		if (typeof window.handleDropdown === 'function') {
			window.handleDropdown();
		}
		// Restaurer les messages d'erreur promo après le refresh
		restorePromoErrorMessage();

		// Restaurer l'ouverture des détails enfants
		openChildrenContainerIds.forEach(id => {
			const container = innerCart.querySelector(`.mm-dc-product-container[data-cart-item-id="${id}"]`);
			if (!container) return;
			const wrapper = container.querySelector('.mm-dc-product-children-wrapper');
			if (!wrapper) return;
			wrapper.classList.remove('visually-hidden');
			const trigger = container.querySelector('.mm-dc-product-children-details-link');
			const textReduce = trigger?.getAttribute('data-text-reduce');
			if (trigger && textReduce) {
				trigger.textContent = textReduce;
			}
		});
	}
	
	// S'assurer que le compteur est toujours à jour après le refresh
	if (data && typeof data.item_count !== 'undefined') {
		updateCartCount(data.item_count);
	} else if (data && data.items) {
		// Calculer item_count depuis les items si non présent
		const calculatedCount = data.items.reduce((sum, item) => sum + item.quantity, 0);
		updateCartCount(calculatedCount);
	} else {
		// Fallback: récupérer le panier pour obtenir le bon item_count
		mmGetCart().then(cartData => {
			if (cartData && typeof cartData.item_count !== 'undefined') {
				updateCartCount(cartData.item_count);
			}
		});
	}
	
	// Dispatch cart updated event
	document.dispatchEvent(new CustomEvent('cart:updated'));
	
	return data;
}

/*********************************************************************************************
 * Met à jour le sélecteur de quantité après un ajout au panier :
 * recalcule le stock restant et met à jour max, boutons et label du CTA.
 *
 * @param {HTMLElement} form - Le formulaire soumis.
 * @param {HTMLElement|null} submitBtn - Le bouton submit du formulaire.
 */
function mmUpdateQttySelectorAfterAdd(form, submitBtn) {
	const qtyInput = form.querySelector('[data-qtty-input]');
	if (!qtyInput) return;

	const maxInventory = parseInt(qtyInput.dataset.maxInventory, 10);
	const seuil = parseInt(qtyInput.dataset.seuil || '0', 10);
	const variantId = parseInt(qtyInput.dataset.variantId, 10);
	const inventoryPolicy = qtyInput.dataset.inventoryPolicy;

	// Pas de limite de stock → toujours réactiver
	if (isNaN(maxInventory) || isNaN(variantId) || inventoryPolicy === 'continue') {
		if (submitBtn) submitBtn.disabled = false;
		return;
	}

	mmGetCart().then(cartData => {
		let cartQty = 0;
		if (cartData && cartData.items) {
			cartData.items.forEach(item => {
				if (item.variant_id == variantId) cartQty += item.quantity;
			});
		}

		const newMax = Math.max(0, maxInventory - seuil - cartQty);
		qtyInput.max = newMax;

		const plusBtn = form.querySelector('[data-qtty-plus]');
		const ctaBtn = submitBtn || qtyInput.closest('.mm-product-quantity-selector-wrapper')?.querySelector('[data-cta] button[type="submit"]');

		if (newMax <= 0) {
			if (plusBtn) plusBtn.setAttribute('disabled', 'true');
			if (ctaBtn) {
				ctaBtn.disabled = true;
				const soldOutLabel = ctaBtn.dataset.soldOutLabel;
				if (soldOutLabel) ctaBtn.textContent = soldOutLabel;
			}
		} else {
			if (plusBtn) plusBtn.removeAttribute('disabled');
			if (ctaBtn) ctaBtn.disabled = false;
		}
	}).catch(() => {
		if (submitBtn) submitBtn.disabled = false;
	});
}

/*********************************************************************************************
 * Sets up the cart form submission handler.
 */
function mmSetupProductForm() {
	const forms = document.querySelectorAll("[data-type='add-to-cart-form']");
	if (!forms || forms.length === 0) return;

	forms.forEach((form) => {
		// Supprimer les anciens event listeners en clonant l'élément
		const newForm = form.cloneNode(true);
		form.parentNode.replaceChild(newForm, form);
		
		newForm.addEventListener("submit", function (event) {
			event.preventDefault();
			event.stopPropagation();
			const timestamp = new Date().toISOString();

			// Bloquer le double-clic pendant que la requête est en cours
			const submitBtn = newForm.querySelector('button[type="submit"]');
			if (submitBtn) submitBtn.disabled = true;

			const formData = new FormData(newForm);
			
			// Récupérer l'ID du variant de manière plus fiable
			// Priorité 1: input spécifique product_form_selected_variant_id
			// Priorité 2: premier input avec name="id" dans le formulaire
			// Priorité 3: FormData.get('id')
			let variantId = newForm.querySelector('#product_form_selected_variant_id')?.value ||
			                newForm.querySelector('input[name="id"]')?.value ||
			                formData.get('id') ||
			                'unknown';
			
			// Vérifier que c'est bien un nombre valide
			variantId = String(variantId).trim();
			if (variantId === 'unknown' || isNaN(Number(variantId))) {
				// Essayer de récupérer depuis le FormData directement
				const allIds = formData.getAll('id');
				if (allIds.length > 0) {
					variantId = allIds[0];
				}
			}
			
			const quantity = formData.get('quantity') || newForm.querySelector('[name="quantity"]')?.value || 1;
			
			
			// S'assurer que le FormData contient bien le bon variant ID
			// Supprimer toutes les entrées 'id' existantes et ajouter la bonne
			formData.delete('id');
			formData.append('id', variantId);
			
			// Demander aussi le rendu de la section panier (+ cart bar) pour MAJ instantanée:
			// garantit un HTML à jour, sans latence/risque de cache d'un fetch séparé.
			const sectionId = mmCartSectionId();
			try {
				formData.append('sections', [sectionId, 'mm-cart-bar'].filter(Boolean).join(','));
			} catch (_) {}

			fetch(window.Shopify.routes.root + "cart/add.js", {
				method: "POST",
				body: formData,
			})
				.then((response) => response.json())
				.then((data) => {
					try {
						const cartBarHtml = data.sections && data.sections['mm-cart-bar'];
						document.dispatchEvent(new CustomEvent('cartbar:add', { detail: { variantId, image: data.image, cartBarHtml } }));
					} catch (_) {}

					const sectionsKeys = data.sections ? Object.keys(data.sections) : [];
					const validSectionsKeys = sectionsKeys.filter(key =>
						key && key !== 'null' && key !== 'undefined' && typeof key === 'string' && key.length > 0
					);

					// Mettre à jour immédiatement le compteur du panier
					if (data && typeof data.item_count !== 'undefined') {
						updateCartCount(data.item_count);
					} else {
						// Fallback: récupérer le cart pour obtenir item_count
						mmGetCart().then(cartData => {
							if (cartData && typeof cartData.item_count !== 'undefined') {
								updateCartCount(cartData.item_count);
							}
						});
					}

					// 1) Rafraîchir uniquement la card source si possible (uniquement si le formulaire vient d'une product card)
					const productCardEl = newForm.closest('.mm-product-card');
					if (productCardEl && typeof window.refreshProductCards === 'function') {
						const pid = parseInt(productCardEl.dataset.productId, 10) || data.product_id;
						window.refreshProductCards(pid, productCardEl);
					}

					// 1bis) Forcer la mise à jour des états CTA/cart-state immédiate
					if (typeof window.mmProductCardsOnCartUpdated === 'function') {
						window.mmProductCardsOnCartUpdated();
					}

					// 1ter) Mettre à jour l'indicateur de quantité sur la page produit principal
					const isProductPage = document.querySelector('.mm-main-product-medias');
					if (isProductPage) {
						// Récupérer l'ID du produit depuis le formulaire
						const productId = document.querySelector('#product_id')?.value || data.product_id;
						if (productId) {
							// Récupérer la quantité totale du produit dans le panier
							mmGetCart().then(cartData => {
								if (cartData && cartData.items) {
									let totalQuantity = 0;
									cartData.items.forEach(item => {
										if (item.product_id == productId) {
											totalQuantity += item.quantity;
										}
									});
									updateMainProductCartIndicator(productId, totalQuantity);
								}
							});
						}
					}

					// 2) Mettre à jour le drawer avec sections si dispo, sinon récupérer le HTML
					let sectionId = mmCartSectionId();
					
					// Si pas de sectionId dans le DOM mais qu'on a des sections dans la réponse, extraire l'ID depuis les clés
					if (!sectionId && data && data.sections && Object.keys(data.sections).length > 0) {
						const sectionsKeys = Object.keys(data.sections).filter(key => 
							key && key !== 'null' && key !== 'undefined' && typeof key === 'string' && key.length > 0
						);
						
						if (sectionsKeys.length > 0) {
							// Chercher une section qui ressemble à une section panier
							const possibleSectionId = sectionsKeys.find(key => 
								key.includes('mm_cart') || key.includes('cart') || key.includes('mm-cart')
							);
							if (possibleSectionId) {
								sectionId = possibleSectionId;
							} else {
								// Prendre la première section valide disponible
								sectionId = sectionsKeys[0];
							}
						} else {
							// Si on n'a pas de clé valide mais qu'on a des sections, on doit récupérer le HTML via l'ID de section connu
							// Utiliser l'ID de section qu'on connaît (celui utilisé dans cart/change.js)
							const knownSectionId = 'template--27372992463222__mm_cart_zBXeHR';
							sectionId = knownSectionId;
						}
					}
					
					// Si on a des sections dans la réponse, les utiliser directement
					if (data && data.sections && Object.keys(data.sections).length > 0) {
						// Si on n'a pas de sectionId mais qu'on a des sections, utiliser la première valide
						if (!sectionId) {
							const sectionsKeys = Object.keys(data.sections).filter(key => 
								key && key !== 'null' && key !== 'undefined' && typeof key === 'string' && key.length > 0
							);
							if (sectionsKeys.length > 0) {
								sectionId = sectionsKeys.find(key => 
									key.includes('mm_cart') || key.includes('cart') || key.includes('mm-cart')
								) || sectionsKeys[0];
							}
						}
						
						if (sectionId && sectionId !== 'null' && data.sections[sectionId]) {
							// Ajouter/retirer les free products si besoin puis rafraîchir
							return mmAddOrRemoveFreeProducts(data)
								.then((updatedData) => {
									return refreshCartFromData(updatedData);
								});
						}
					}
					
					// Si on a un sectionId mais pas de sections dans la réponse, récupérer le HTML
					if (sectionId) {
						return fetch(`${window.Shopify.routes.root}?section_id=${sectionId}`)
							.then(res => res.text())
							.then((html) => {
								const tempData = { sections: {} };
								tempData.sections[sectionId] = html;
								return mmAddOrRemoveFreeProducts(tempData)
									.then((updatedData) => {
										return refreshCartFromData(updatedData);
									});
							})
							.catch((error) => {
								// Fallback: juste mettre à jour le compteur
								if (data && typeof data.item_count !== 'undefined') {
									updateCartCount(data.item_count);
								}
								document.dispatchEvent(new CustomEvent('cart:updated'));
								return data;
							});
					} else {
						// Pas de section panier dans le DOM, fallback léger: MAJ compteur + event
						if (data && typeof data.item_count !== 'undefined') {
							updateCartCount(data.item_count);
						} else {
							// Récupérer le panier pour avoir le bon item_count
							mmGetCart().then(cartData => {
								if (cartData && typeof cartData.item_count !== 'undefined') {
									updateCartCount(cartData.item_count);
								}
							});
						}
						// Popup tout de suite
						if (typeof showAddToCartPopup === 'function') {
							showAddToCartPopup(data);
						}

						document.dispatchEvent(new CustomEvent('cart:updated'));
						return data;
					}
				})
				.catch((error) => {
					if (submitBtn) submitBtn.disabled = false;
				})
				.finally(() => {
					mmUpdateQttySelectorAfterAdd(newForm, submitBtn);
				});
		});
	});
}

/*********************************************************************************************
 * Refresh the cart and open the cart drawer
 */
function mmOpenAndRefreshCart() {
	function handleResponse() {
		const newCartEl = document.createElement("DIV");
		newCartEl.innerHTML = this.responseText;
		const innerCart = mmCartSectionElement().querySelector(".mm-drawer-cart");
		innerCart.innerHTML = newCartEl.querySelector(".mm-drawer-cart").innerHTML;

		mmOpenCart();
		
		// Réinitialiser les dropdowns après re-render
		if (typeof window.handleDropdown === 'function') {
			window.handleDropdown();
		}
		// Restaurer les messages d'erreur promo après le refresh
		restorePromoErrorMessage();
		
		// Dispatch cart updated event
		document.dispatchEvent(new CustomEvent('cart:updated'));
	}
	const request = new XMLHttpRequest();
	request.addEventListener("load", handleResponse);
	request.open("GET", `${window.Shopify.routes.root}?section_id=${mmCartSectionId()}`, true);
	request.send();
}

/*********************************************************************************************
 * Increase the quantity of a specific product in the cart by a specified amount.
 *
 * @param {number} line_index - The line item index.
 * @param {number} [increment=1] - The quantity to add (defaults to 1).
 */
function mmIncreaseProductQuantity(line_index, increment = 1) {
	const itemCart = mmCartItem(line_index);
	if (!itemCart) return;
	const quantity = mmGetCartItemQuantity(line_index);
	if (quantity == null) return;
	const maxQtty = parseInt(itemCart.dataset.maxQuantity, 10);
	const newQuantity = quantity + increment;

	if (newQuantity > maxQtty) {
		return;
	}

	mmSetCartItemQuantity(line_index, newQuantity);
	mmDebouncedPushNewQuantity(line_index);
}

/*********************************************************************************************
 * Decrease the quantity of a specific product in the cart by one.
 *
 * @param {number} line_index - The line item index.
 */
function mmDecreaseProductQuantity(line_index) {
	const itemCart = mmCartItem(line_index);
	if (!itemCart) return;
	const quantity = mmGetCartItemQuantity(line_index);
	if (quantity == null) return;
	const maxQtty = parseInt(itemCart.dataset.maxQuantity, 10);
	const newQuantity = quantity - 1;

	if (newQuantity > maxQtty) {
		return;
	}

	mmSetCartItemQuantity(line_index, newQuantity);
	mmDebouncedPushNewQuantity(line_index);
}

/*********************************************************************************************
 * Remove a specific product from the cart.
 *
 * @param {number} line_index - The line item index.
 */
function mmDestroyProductFromCart(line_index) {
	const itemEl = mmCartItem(line_index);
	const newQuantity = 0;
	mmSetCartItemQuantity(line_index, newQuantity);
	// Appeler directement l'API sans debounce pour la suppression immédiate
	// Cela évite les problèmes de timing si l'utilisateur navigue rapidement
	mmPushNewQuantity(line_index);
}

/*********************************************************************************************
 * Remove every line of a Recharge bundle (same `_rc_bundle` property) from the cart.
 *
 * @param {string} bundleId - The `_rc_bundle` property shared by the bundle lines.
 */
function mmDestroyBundleFromCart(bundleId) {
	mmAPIRequest("GET", "cart.js")
		.then((cart) => {
			const updates = {};
			cart.items.forEach((item) => {
				if (item.properties && item.properties._rc_bundle === bundleId) {
					updates[item.key] = 0;
				}
			});
			return mmAPIRequest("POST", "cart/update.js", { updates, sections: mmCartSectionId() });
		})
		.then(mmAddOrRemoveFreeProducts)
		.then(refreshCartFromData)
		.then((data) => {
			if (data && typeof data.item_count !== 'undefined') {
				updateCartCount(data.item_count);
			}
			return data;
		})
		.catch((error) => {
			try { document.dispatchEvent(new CustomEvent('cart:error', { detail: { source: 'destroy-bundle', error } })); } catch (_) {}
			try { mmOpenAndRefreshCart(); } catch (_) { try { mmOpenCart(); } catch (_) {} }
		});
}

/*********************************************************************************************
 * Push the new quantity of a specific product to the server.
 *
 * @param {number} line_index - The line item index.
 */
function mmPushNewQuantity(line_index) {
	const quantity = mmGetCartItemQuantity(line_index);
	// Si quantity est null (produit avec enfants), utiliser 0 pour supprimer
	const finalQuantity = quantity !== null ? quantity : 0;
	
	mmAPIRequest("POST", "cart/change.js", {
		line: line_index,
		quantity: finalQuantity,
		sections: mmCartSectionId(),
	})
		.then((data) => {
			return data;
		})
		.then(mmAddOrRemoveFreeProducts)
		.then((data) => {
			// S'assurer que le compteur est mis à jour après mmAddOrRemoveFreeProducts
			if (data && typeof data.item_count !== 'undefined') {
				updateCartCount(data.item_count);
			} else if (data && data.items) {
				// Calculer item_count depuis les items si non présent
				const calculatedCount = data.items.reduce((sum, item) => sum + item.quantity, 0);
				updateCartCount(calculatedCount);
			} else {
				// Fallback: récupérer le panier pour obtenir le bon item_count
				mmGetCart().then(cartData => {
					if (cartData && typeof cartData.item_count !== 'undefined') {
						updateCartCount(cartData.item_count);
					}
				});
			}
			
			return data;
		})
		.then(refreshCartFromData)
		.then((data) => {
			// S'assurer que le compteur est toujours à jour après refreshCartFromData
			if (data && typeof data.item_count !== 'undefined') {
				updateCartCount(data.item_count);
			} else if (data && data.items) {
				// Calculer item_count depuis les items si non présent
				const calculatedCount = data.items.reduce((sum, item) => sum + item.quantity, 0);
				updateCartCount(calculatedCount);
			} else {
				// Fallback: récupérer le panier pour obtenir le bon item_count
				mmGetCart().then(cartData => {
					if (cartData && typeof cartData.item_count !== 'undefined') {
						updateCartCount(cartData.item_count);
					}
				});
			}
			
			// Mettre à jour l'indicateur de quantité sur la page produit principal
			if (data && data.items) {
				// Trouver le produit correspondant à cette ligne
				const cartItem = data.items.find(item => item.line === line_index);
				if (cartItem) {
					updateMainProductCartIndicator(cartItem.product_id, finalQuantity);
				}
			}
			return data;
		})
		.catch((error) => {
			try { document.dispatchEvent(new CustomEvent('cart:error', { detail: { source: 'change-debounced', error } })); } catch (_) {}
			try { mmOpenAndRefreshCart(); } catch (_) { try { mmOpenCart(); } catch (_) {} }
		});
}

/*********************************************************************************************
 * Remove a line item from the cart.
 *
 * @param {number|string} line_index - The index of the line item in the cart to be removed.
 */
function mmDestroyLineItemFromCart(line_index) {
	mmAPIRequest("POST", "cart/change.js", {
		line: parseInt(line_index),
		quantity: 0,
		sections: mmCartSectionId(),
	})
	.then(mmAddOrRemoveFreeProducts)
	.then(refreshCartFromData)
	.then((data) => {
		// S'assurer que le compteur est toujours à jour
		if (data && typeof data.item_count !== 'undefined') {
			updateCartCount(data.item_count);
		} else if (data && data.items) {
			// Calculer item_count depuis les items si non présent
			const calculatedCount = data.items.reduce((sum, item) => sum + item.quantity, 0);
			updateCartCount(calculatedCount);
		} else {
			// Fallback: récupérer le panier pour obtenir le bon item_count
			mmGetCart().then(cartData => {
				if (cartData && typeof cartData.item_count !== 'undefined') {
					updateCartCount(cartData.item_count);
				}
			});
		}
		
		// Mettre à jour l'indicateur de quantité sur la page produit principal
		if (data && data.items) {
			// Trouver le produit correspondant à cette ligne
			const cartItem = data.items.find(item => item.line === parseInt(line_index));
			if (cartItem) {
				updateMainProductCartIndicator(cartItem.product_id, 0);
			}
		}
	})
	.catch((error) => {
		try { document.dispatchEvent(new CustomEvent('cart:error', { detail: { source: 'destroy-line-item', error } })); } catch (_) {}
		try { mmOpenAndRefreshCart(); } catch (_) { try { mmOpenCart(); } catch (_) {} }
	});
}

/*********************************************************************************************
 * Debounced function to push new quantity of a specific product to the server.
 *
 * @param {number} line_index - The line item index.
 */
const mmDebouncedPushNewQuantity = debounce(function (line_index, maxQtty) {
	const quantity = mmGetCartItemQuantity(line_index);

	if (quantity > maxQtty) {
		return;
	}
	mmPushNewQuantity(line_index);
}, 300);

/*********************************************************************************************
 * Execute a callback once the DOM is ready (or immediately if it already is).
 */
function mmOnDomReady(callback) {
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', callback, { once: true });
	} else {
		callback();
	}
}

/*********************************************************************************************
 * Update the cart count bubble with the given value.
 *
 * @param {number} value - The new cart count value.
 */
function updateCartCount(value) {
	const bubbles = Array.from(document.querySelectorAll(".mm-cart-count-bubble"));
	if (!bubbles.length) {
		return;
	}

	bubbles.forEach((cartCountBubble) => {
		if (value > 0) {
			cartCountBubble.classList.remove("mm-none");

			// Récupérer l'ancienne valeur
			const oldValue = parseInt(cartCountBubble.dataset.cartCount) || 0;
			const newValue = value;

			// Déterminer la direction de l'animation
			let animationDirection = '';
			if (newValue > oldValue) {
				animationDirection = 'slide-up';
			} else if (newValue < oldValue) {
				animationDirection = 'slide-down';
			}

			// Si la valeur change, déclencher l'animation de glissement
			if (animationDirection && oldValue !== newValue) {
				cartCountBubble.classList.add(animationDirection);
				setTimeout(() => {
					const numberElement = cartCountBubble.querySelector('.mm-cart-count-number');
					if (numberElement) {
						numberElement.textContent = newValue;
					}
					cartCountBubble.dataset.cartCount = newValue;
					cartCountBubble.classList.remove(animationDirection);
				}, 150);
			} else {
				// Pas d'animation nécessaire, mise à jour directe
				const numberElement = cartCountBubble.querySelector('.mm-cart-count-number');
				if (numberElement) {
					numberElement.textContent = newValue;
				}
				cartCountBubble.dataset.cartCount = newValue;
			}

			// Déclencher l'animation jelly
			cartCountBubble.classList.remove("jelly-animation");
			// Forcer un reflow
			cartCountBubble.offsetHeight;
			cartCountBubble.classList.add("jelly-animation");
			setTimeout(() => {
				cartCountBubble.classList.remove("jelly-animation");
			}, 500);
		} else {
			// Valeur 0: masquer et réinitialiser le texte/dataset
			const numberElement = cartCountBubble.querySelector('.mm-cart-count-number');
			if (numberElement) {
				numberElement.textContent = '0';
			}
			cartCountBubble.dataset.cartCount = 0;
			cartCountBubble.classList.add("mm-none");
		}
	});

	// if value == 0, on supprimer l'entrée dans le localstorage
	if (value == 0) {
        localStorage.removeItem('doNotAddThisVariant');
    }
}

/*********************************************************************************************
 * Add or remove free products from the cart based on conditions.
 *
 * @param {Object} data - The data containing the new cart HTML.
 * @returns {Object} - The response data.
 */
function mmAddOrRemoveFreeProducts(data) {
    const timestamp = new Date().toISOString();
    let sectionId = mmCartSectionId();
    
    // Si pas de sectionId dans le DOM mais qu'on a des sections dans la réponse, extraire l'ID depuis les clés
    if (!sectionId && data && data.sections && Object.keys(data.sections).length > 0) {
        const sectionsKeys = Object.keys(data.sections).filter(key => 
            key && key !== 'null' && key !== 'undefined' && typeof key === 'string' && key.length > 0
        );
        
        if (sectionsKeys.length > 0) {
            // Chercher une section qui ressemble à une section panier
            const possibleSectionId = sectionsKeys.find(key => 
                key.includes('mm_cart') || key.includes('cart') || key.includes('mm-cart')
            );
            if (possibleSectionId) {
                sectionId = possibleSectionId;
            } else {
                // Prendre la première section valide disponible
                sectionId = sectionsKeys[0];
            }
        }
    }
    
    if (!sectionId || !data || !data.sections || !data.sections[sectionId]) {
        // Retourner une Promise résolue pour être cohérent
        return Promise.resolve(data);
    }

    const el = mmCartHTMLFromData(data);
    if (!el) {
        return Promise.resolve(data);
    }

    const freeProductsContainer = el.querySelector(CART_CONFIG.selectors.freeProducts);
    if (!freeProductsContainer || !freeProductsContainer.children || freeProductsContainer.children.length === 0) {
        return Promise.resolve(data);
    }

    const updates = {};
    const freeProductsInfo = [];
    
    Array.from(freeProductsContainer.children).forEach((freeProductEl) => {
        const variantIdRaw = freeProductEl.dataset.variantId;
        const variantId = variantIdRaw && String(variantIdRaw).trim();
        if (!variantId || isNaN(Number(variantId))) {
            return; // ignorer les IDs invalides
        }
        const shouldHave = freeProductEl.dataset.freeProduct === "true";
        const hasIt = !!document.querySelector(CART_CONFIG.selectors.cartItemById.replace("{{id}}", variantId));
        
        freeProductsInfo.push({
            variantId,
            shouldHave,
            hasIt
        });

        if (hasIt && !shouldHave) {
            updates[variantId] = 0;
        } else if (!hasIt && shouldHave) {
			// on fait ça que si on a pas dans le localstorage 'ne plus ajouter le produit : 03565412'
			const variantIdToAvoid = localStorage.getItem('doNotAddThisVariant');
			if (variantIdToAvoid == variantId) {
			} else {
				updates[variantId] = 1;
			}
        }
    });

    if (Object.keys(updates).length > 0) {
        const body = { updates, sections: sectionId };
        return mmAPIRequest("POST", "cart/update.js", body)
            .then((responseData) => {
                // S'assurer que le compteur est mis à jour après l'ajout/retrait de produits gratuits
                if (responseData && typeof responseData.item_count !== 'undefined') {
                    updateCartCount(responseData.item_count);
                } else if (responseData && responseData.items) {
                    // Calculer item_count depuis les items si non présent
                    const calculatedCount = responseData.items.reduce((sum, item) => sum + item.quantity, 0);
                    updateCartCount(calculatedCount);
                }
                
                return responseData;
            })
            .then(refreshCartFromData)
            .catch((error) => {
                try { document.dispatchEvent(new CustomEvent('cart:error', { detail: { source: 'free-products-update', error } })); } catch (_) {}
                return data;
            });
    }

    // Toujours retourner une Promise pour être cohérent
    return Promise.resolve(data);
}

function mmDestroyFreeProductFromCart(variantId, lineIndex) {
	// ajouter cle/valeur dans localStorage 'ne plus ajouter le produit : 03565412'
	localStorage.setItem('doNotAddThisVariant', variantId);

	// supprimer le produit du panier
	mmDestroyProductFromCart(lineIndex);
}

/*********************************************************************************************
 * Vérifie au chargement si un produit offert doit être ajouté/retiré
 * en fonction du seuil et met à jour le panier si besoin.
 */
function mmCheckFreeProductsOnLoad() {
    const sectionId = mmCartSectionId();
    if (!sectionId) return;

    // Récupérer le HTML de la section panier pour lire les données free-products
    fetch(`${window.Shopify.routes.root}?section_id=${sectionId}`)
        .then(res => res.text())
        .then((html) => {
            const tempData = { sections: {} };
            tempData.sections[sectionId] = html;
            // Utiliser la logique existante pour ajouter/retirer les produits offerts
            return mmAddOrRemoveFreeProducts(tempData);
        })
        .catch((error) => {
        });
}

/*********************************************************************************************
 * Add a free product to the cart and optionally open the cart drawer.
 *
 * @param {number} productId - The free product ID to add.
 * @param {Object} options - Options for adding the product.
 * @param {number} [options.quantity=1] - The quantity to add.
 * @param {boolean} [options.openCart=true] - Whether to open the cart drawer after adding the product.
 * @param {HTMLElement} clickedElem - The element that was clicked to trigger this action.
 */
function mmAddFreeProductToCart(productId, { quantity = 1, openCart = true }, clickedElem) {
	// Debounce on the CTA
	clickedElem.innerText = "AJOUTÉ !";
	clickedElem.classList.add("mm-disabled");

	// Ajouter le free product
	mmAddProductToCart(productId, { openCart: false });
}

/*********************************************************************************************
 * Force la synchronisation du compteur du panier avec le panier réel
 */
function mmSyncCartCount() {
	mmGetCart().then(cartData => {
		if (cartData && typeof cartData.item_count !== 'undefined') {
			updateCartCount(cartData.item_count);
		} else if (cartData && cartData.items) {
			// Calculer item_count depuis les items si non présent
			const calculatedCount = cartData.items.reduce((sum, item) => sum + item.quantity, 0);
			updateCartCount(calculatedCount);
		}
	}).catch(error => {
	});
}

/*********************************************************************************************
 * Loaded Listenner
 *
 */
// Synchroniser le compteur immédiatement si le DOM est déjà prêt
if (document.readyState === 'loading') {
	window.addEventListener("DOMContentLoaded", (event) => {
		// Synchroniser le compteur au chargement pour s'assurer qu'il est correct
		mmSyncCartCount();
		// Synchroniser à nouveau après un court délai pour s'assurer que c'est bien fait
		setTimeout(() => mmSyncCartCount(), 300);
	});
} else {
	// Le DOM est déjà prêt, synchroniser immédiatement
	mmSyncCartCount();
	// Synchroniser à nouveau après un court délai pour s'assurer que c'est bien fait
	setTimeout(() => mmSyncCartCount(), 300);
}

// Synchroniser le compteur quand la page redevient visible (retour d'onglet, navigation, etc.)
document.addEventListener('visibilitychange', () => {
	if (!document.hidden) {
		// La page est devenue visible, synchroniser le compteur
		mmSyncCartCount();
	}
});

// Synchroniser le compteur lors de la navigation (pageshow pour gérer le cache du navigateur)
window.addEventListener('pageshow', (event) => {
	// Toujours synchroniser le compteur au pageshow, même si la page ne vient pas du cache
	// Cela garantit que le compteur est à jour après une navigation
	mmSyncCartCount();
});

mmOnDomReady(() => {
	mmInitSplideRea();
    mmInitSplideCross();
    mmSetupProductForm();
    mmSetupChildrenDetailsToggle();
    mmCheckSoloRestriction();
    // Vérifier immédiatement les produits offerts au chargement de la page panier
    mmCheckFreeProductsOnLoad();
    
    // Test de l'indicateur de quantité sur la page produit
    if (document.querySelector('.mm-main-product-medias')) {
        // Vérifier si le produit est déjà dans le panier au chargement
        const productForm = document.querySelector('#product-form');
        if (productForm) {
            const productId = document.querySelector('#product_id')?.value;
            if (productId) {
                mmGetCart().then(cartData => {
                    if (cartData && cartData.items) {
                        let totalQuantity = 0;
                        cartData.items.forEach(item => {
                            if (item.product_id == productId) {
                                totalQuantity += item.quantity;
                            }
                        });
                        if (totalQuantity > 0) {
                            updateMainProductCartIndicator(productId, totalQuantity);
                        }
                    }
                });
            }
        }
    }
});

/*********************************************************************************************
* Mettre à jour l'indicateur de quantité dans le panier sur la page produit principal
*/
function updateMainProductCartIndicator(productId, newQuantity) {
	// Trouver l'élément d'indicateur de quantité dans les médias du produit principal
	const cartIndicator = document.querySelector('.mm-main-product-media-number-product-in-cart');
	
	if (cartIndicator) {
		if (newQuantity > 0) {
			// Afficher l'indicateur avec la nouvelle quantité
			cartIndicator.textContent = `x${newQuantity}`;
			cartIndicator.style.display = 'flex';
		} else {
			// Masquer l'indicateur si la quantité est 0
			cartIndicator.style.display = 'none';
		}
	} else {
		// Essayer de créer l'élément s'il n'existe pas
		createMainProductCartIndicator(newQuantity);
	}
}

function createMainProductCartIndicator(quantity) {
	// Trouver le conteneur des médias
	const mediaContainer = document.querySelector('.mm-main-product-medias .splide__slide:first-child');
	if (mediaContainer && quantity > 0) {
		// Créer l'élément indicateur
		const indicator = document.createElement('div');
		indicator.className = 'mm-main-product-media-number-product-in-cart';
		indicator.textContent = `x${quantity}`;
		indicator.style.cssText = `
			position: absolute;
			top: 0;
			left: 0;
			right: 0;
			bottom: 0;
			background: rgba(0, 0, 0, 0.4);
			display: flex;
			align-items: center;
			justify-content: center;
			color: white;
			font-size: var(--heading-3);
			font-weight: var(--font-regular);
			line-height: 95%;
			letter-spacing: -2px;
			font-family: var(--header-font);
			z-index: 10;
		`;
		
		// Ajouter l'indicateur au conteneur
		mediaContainer.style.position = 'relative';
		mediaContainer.appendChild(indicator);
	}
}

/*********************************************************************************************
* Fonction de rafraîchissement des cartes de produit
*/
async function refreshProductCards(productId, sourceCard = null) {
  try {
    // Si on a une source card, utiliser son handle directement
    let handle;
    if (sourceCard && sourceCard.dataset.productHandle) {
      handle = sourceCard.dataset.productHandle;
    } else {
      // Sinon, cibler toutes les cards de ce produit et récupérer le handle
      const cards = Array.from(
        document.querySelectorAll(`.mm-product-card[data-product-id="${productId}"]`)
      );
      if (!cards.length) return;
      
      handle = cards[0].dataset.productHandle;
    }
    
    if (!handle) {
      return;
    }

    // Approche: récupérer le HTML frais depuis le serveur pour un vrai refresh
    
    // 3) Récupérer le HTML de la carte de produit via le template card
    const url = `${window.Shopify.routes.root}products/${encodeURIComponent(handle)}?view=card`;
    const res = await fetch(url, { credentials: 'same-origin' });
    if (!res.ok) throw new Error(`HTTP ${res.status} on ${url}`);
    const html = await res.text();

    // 4) Parser le HTML et trouver la carte du produit
    const tmp = document.createElement('div');
    tmp.innerHTML = html.trim();
    
    // Chercher la carte du produit avec le bon handle
    let freshCard = tmp.querySelector(`[data-product-handle="${handle}"]`);
    
    if (!freshCard) {
      // Essayer de trouver une carte de produit générique
      freshCard = tmp.querySelector('.mm-product-card');
    }
    
    if (!freshCard) {
      return;
    }
    
    // Vérification: la carte doit correspondre au bon handle
    if (freshCard.dataset.productHandle !== handle) {
      return;
    }

    // 5) Cibler toutes les cartes du produit à rafraîchir
    const cardsToUpdate = Array.from(
      document.querySelectorAll(`.mm-product-card[data-product-handle="${handle}"]`)
    );
    
    if (cardsToUpdate.length === 0) {
      return;
    }

    // 6) Remplacer chaque carte par la version fraîche du serveur
    const newCards = [];
    
    cardsToUpdate.forEach((oldCard) => {
      // Cloner la carte fraîche du serveur
      const newCard = freshCard.cloneNode(true);

      // Remplacer l'ancienne carte par la nouvelle
      oldCard.replaceWith(newCard);
      
      // Stocker la nouvelle carte pour l'initialisation
      newCards.push(newCard);

      // Déclencher un event custom
      document.dispatchEvent(new CustomEvent('product-card:refreshed', {
        detail: { productId, handle, oldEl: oldCard, newEl: newCard }
      }));
    });

    // 7) Réinitialiser les scripts sur les nouvelles cartes
    newCards.forEach((newCard) => {
      if (typeof window.mmSetupProductFormForSection === 'function') {
        window.mmSetupProductFormForSection(newCard);
      }
      if (typeof window.mmSetupProductCardVariantsForSection === 'function') {
        window.mmSetupProductCardVariantsForSection(newCard);
      }
    });

    // Si tu as des sliders reloadables
    if (typeof window.reinitReloadableSplides === 'function') {
      window.reinitReloadableSplides();
    }

    // 5) Forcer la mise à jour visuelle de toutes les cartes du produit
    if (typeof window.mmProductCardsOnCartUpdated === 'function') {
      window.mmProductCardsOnCartUpdated();
    }
  } catch (err) {
  }
}

/*********************************************************************************************
* Sets up the cart form submission handler for a specific section.
*/
function mmSetupProductFormForSection(section) {
const forms = section.querySelectorAll("[data-type='add-to-cart-form']");
if (!forms) return;

forms.forEach((form) => {
	// Supprimer les anciens event listeners en clonant l'élément
	const newForm = form.cloneNode(true);
	form.parentNode.replaceChild(newForm, form);

		newForm.addEventListener("submit", function (event) {
		event.preventDefault();
		event.stopPropagation();
		
		const formData = new FormData(newForm);
		const variantId = formData.get('id') || newForm.querySelector('[name="id"]')?.value || 'unknown';
		const quantity = formData.get('quantity') || newForm.querySelector('[name="quantity"]')?.value || 1;

		try {
			const sectionId = mmCartSectionId();
			formData.append('sections', [sectionId, 'mm-cart-bar'].filter(Boolean).join(','));
		} catch (_) {}

		fetch(window.Shopify.routes.root + "cart/add.js", {
		method: "POST",
		body: formData,
		})
		.then((response) => response.json())
		.then((data) => {
			try {
				const cartBarHtml = data.sections && data.sections['mm-cart-bar'];
				document.dispatchEvent(new CustomEvent('cartbar:add', { detail: { variantId, image: data.image, cartBarHtml } }));
			} catch (_) {}

			// Mettre à jour immédiatement le compteur du panier
			if (data && typeof data.item_count !== 'undefined') {
				updateCartCount(data.item_count);
			} else {
				// Fallback: récupérer le cart pour obtenir item_count
				mmGetCart().then(cartData => {
					if (cartData && typeof cartData.item_count !== 'undefined') {
						updateCartCount(cartData.item_count);
					}
				});
			}
			// Mise à jour immédiate des cartes de produit
			// Extraire l'ID du produit depuis la réponse de l'API
			const productId = data.product_id || data.id;
			if (productId) {
				// Passer la card source pour récupérer le bon handle
				const sourceCard = newForm.closest('.mm-product-card');
				refreshProductCards(productId, sourceCard);
			}
			
			// Mettre à jour le contenu du panier sans l'ouvrir
			const sectionId = mmCartSectionId();
			if (sectionId) {
				// Sauvegarder la position de scroll si on est dans le cross-sell
				const isInCrossSell = newForm.closest('.mm-dc-cross-sell-container');
				let scrollPosition = null;
				if (isInCrossSell) {
					scrollPosition = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop;
				}
				
				fetch(`${window.Shopify.routes.root}?section_id=${sectionId}`)
					.then(res => res.text())
					.then((html) => {
						const tempData = { sections: {} };
						tempData.sections[sectionId] = html;
						mmAddOrRemoveFreeProducts(tempData);
						return refreshCartFromData(tempData);
					})
					.then(() => {
						// Restaurer la position de scroll si on était dans le cross-sell
						if (isInCrossSell && scrollPosition !== null) {
							// Utiliser requestAnimationFrame pour s'assurer que le DOM est mis à jour
							requestAnimationFrame(() => {
								window.scrollTo(0, scrollPosition);
							});
						}
					})
					.catch((error) => {
						// Restaurer la position de scroll même en cas d'erreur
						if (isInCrossSell && scrollPosition !== null) {
							requestAnimationFrame(() => {
								window.scrollTo(0, scrollPosition);
							});
						}
					});
			}
		})
		.catch((error) => {
		});
	});
	});
}

// DISCOUNT CODE
function removeDiscount() {
	fetch(window.Shopify.routes.root + 'cart/update.js', {
	  method: 'POST',
	  headers: {
		'Content-Type': 'application/json'
	  },
	  body: JSON.stringify({ discount: '' })
	})
	  .then(res => res.json())
	  .then(data => {
		// Utiliser la fonction custom de rafraîchissement du panier
		mmOpenAndRefreshCart();
	  })
	  .catch(err => {
		alert('Erreur lors du retrait.', false);
	  });
  }
  
  /**
   * Attend que le code promo soit bien appliqué dans le panier (max retries).
   * @param {string} code - Le code promo à attendre.
   * @param {number} retries - Nombre d’essais restants.
   * @param {number} delay - Délai entre chaque essai (ms).
   * @returns {Promise}
   */
  function waitForDiscountInCart(code, retries = 10, delay = 300) {
	return new Promise((resolve, reject) => {
	  function check(remaining) {
		fetch('/cart.js')
		  .then(r => r.json())
		  .then(cart => {
			const found = cart.discount_codes.some(d => d.code.toLowerCase() === code && d.applicable);
			if (found) {
			  resolve();
			} else if (remaining > 0) {
			  setTimeout(() => check(remaining - 1), delay);
			} else {
			  reject('Le code promo n’a pas été appliqué à temps.');
			}
		  })
		  .catch(reject);
	  }
	  check(retries);
	});
  }
  
  async function mmApplyDiscountInCart() {
	const codeInput = document.getElementById('discount-code');
	const newCode = codeInput.value.trim().toLowerCase();
	const errorMessageText = codeInput.dataset.errorText;
	if (!newCode) return;
	
	// S'assurer que le dropdown reste ouvert pendant le traitement
	const discountToggle = document.querySelector('.mm-discount-toggle');
	const discountContent = document.getElementById('discount-content');
	let wasDropdownOpen = false;
	if (discountToggle && discountContent) {
		wasDropdownOpen = discountToggle.classList.contains('active');
		if (!wasDropdownOpen) {
			discountToggle.classList.add('active');
			discountToggle.setAttribute('aria-expanded', 'true');
		}
		// Toujours mettre à jour la max-height pour s'assurer que le contenu soit visible
		updateDiscountDropdownHeight();
	}
  
	// 1. Récupérer tous les codes déjà appliqués
	let previousCodes = [];
	try {
	  const cartBefore = await fetch('/cart.js').then(r => r.json());
	  // on ne garde que ceux applicables
	  previousCodes = cartBefore.discount_codes
		.filter(d => d.applicable)
		.map(d => d.code.toLowerCase());
	} catch (err) {
	}
  
	// 2. Ajouter le produit lié si besoin (comme avant)
	const hiddenInputs = document.querySelectorAll('.mm-hgjfidu');
	let matchedVariantId = null;
	hiddenInputs.forEach(input => {
	  if (atob(input.value).toLowerCase() === newCode) {
		matchedVariantId = input.dataset.variantId;
	  }
	});
	if (matchedVariantId) {
	  try {
		const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
		  method: 'POST',
		  headers: { 'Content-Type': 'application/json' },
		  body: JSON.stringify({ id: matchedVariantId, quantity: 1 })
		});
		const data = await response.json();
	  } catch (err) {
	  }
	}
  
	// 3. Composer la liste complète de codes à appliquer
	const allCodes = Array.from(new Set([...previousCodes, newCode]));
	const discountString = allCodes.join(',');
  
	// 4. Tenter d’appliquer tous les codes
	try {
	  await fetch(window.Shopify.routes.root + 'cart/update.js', {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ discount: discountString })
	  });
  
	  // 5. Vérifier si le nouveau code a été appliqué
	  const cartAfter = await fetch('/cart.js').then(r => r.json());
	  const appliedCodes = cartAfter.discount_codes.map(d => ({
		code: d.code.toLowerCase(),
		ok: d.applicable
	  }));
	  const newCodeResult = appliedCodes.find(d => d.code === newCode);
  
	  if (newCodeResult && newCodeResult.ok) {
		// Nettoyer les messages d'erreur précédents
		localStorage.removeItem('promoErrorMessage');
		const existingError = document.querySelector('.promo-error');
		if (existingError) {
			existingError.remove();
		}
		
		// Fermer le dropdown seulement s'il était fermé initialement
		if (discountToggle && discountContent && !wasDropdownOpen) {
			discountToggle.classList.remove('active');
			discountToggle.setAttribute('aria-expanded', 'false');
			discountContent.style.maxHeight = null;
		}
		
		// On attend que le code promo soit bien visible dans le panier avant de rafraîchir le drawer
		waitForDiscountInCart(newCode, 10, 300)
		  .then(() => {
			mmOpenAndRefreshCart();
		  })
		  .catch(() => {
			// Si jamais le code n'est pas visible après 10 essais, on force le refresh
			mmOpenAndRefreshCart();
		  });
	  } else {
		// échec pour le nouveau code
		const formattedMessage = `${newCode.toUpperCase()} - ${errorMessageText || 'Code non valable'}`;
		displayPromoError(formattedMessage);
  
		// 6. Réappliquer **seulement** les anciens codes
		if (previousCodes.length) {
		  await fetch(window.Shopify.routes.root + 'cart/update.js', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ discount: previousCodes.join(',') })
		  });
		}
		
		// Ne pas rafraîchir le panier en cas d'erreur pour garder le message visible
		return;
	  }
	} catch (err) {
	  displayPromoError('Une erreur est survenue. Veuillez réessayer.');
	  // on ne touche pas aux codes déjà en place
	}
  }
  
  // Affiche un message d'erreur sous le formulaire
  function displayPromoError(message) {
	const container = document.getElementById('discount-form') || document.body;
	let errorElem = container.querySelector('.promo-error');
	if (!errorElem) {
	  const tpl = document.getElementById('promo-error-template');
	  if (tpl && 'content' in tpl) {
		errorElem = tpl.content.firstElementChild.cloneNode(true);
	  } else {
		// Fallback si le template n'est pas présent
		errorElem = document.createElement('div');
		errorElem.className = 'promo-error mm-flex mm-align-center mm-gap-2';
		const p = document.createElement('p');
		p.className = 'promo-error-message mm-config-red mm-text-xs-bold';
		errorElem.appendChild(p);
	  }
	  container.appendChild(errorElem);
	}
	const codeEl = errorElem.querySelector('.promo-error-code');
	if (codeEl) {
	  // Si on reçoit déjà un message formaté "CODE - ...", extraire le code avant le tiret
	  const codeText = String(message || '').split(' - ')[0].toUpperCase();
	  codeEl.textContent = codeText;
	} else {
	  // Fallback ancien comportement (pas de template)
	  const messageEl = errorElem.querySelector('.promo-error-message') || errorElem;
	  messageEl.textContent = message;
	}
	
	// Stocker le message d'erreur dans le localStorage pour persistance après refresh
	if (message) {
	  localStorage.setItem('promoErrorMessage', message);
	}
	
	// Forcer l'ouverture du dropdown des codes promo pour que le message soit visible
	const discountToggle = document.querySelector('.mm-discount-toggle');
	const discountContent = document.getElementById('discount-content');
	if (discountToggle && discountContent) {
		// Ouvrir le dropdown si il n'est pas déjà ouvert
		if (!discountToggle.classList.contains('active')) {
			discountToggle.classList.add('active');
			discountToggle.setAttribute('aria-expanded', 'true');
		}
		// Toujours mettre à jour la max-height après l'ajout du message d'erreur
		updateDiscountDropdownHeight();
	}
}

// Fonction utilitaire pour mettre à jour la max-height du dropdown des codes promo
function updateDiscountDropdownHeight() {
	const discountContent = document.getElementById('discount-content');
	if (discountContent) {
		discountContent.style.maxHeight = `${discountContent.scrollHeight}px`;
	}
}

// Restaure les messages d'erreur promo après le rafraîchissement du panier
function restorePromoErrorMessage() {
	const storedMessage = localStorage.getItem('promoErrorMessage');
	if (storedMessage) {
		// Attendre un peu que le DOM soit prêt après le refresh
		setTimeout(() => {
			displayPromoError(storedMessage);
			// Nettoyer le localStorage après affichage
			localStorage.removeItem('promoErrorMessage');
			
			// Mettre à jour la max-height après la restauration du message
			updateDiscountDropdownHeight();
		}, 100);
	}
}

/*********************************************************************************************
 * Vérifie si le panier contient uniquement des produits solo-interdit et bloque le checkout.
 * La restriction est calculée côté Liquid (accès aux tags) via [data-solo-restriction].
 * Bloque aussi le checkout pour la Suisse hors montant min / max (avant remises), calculé côté Liquid via [data-ch-limit].
 */
function mmCheckSoloRestriction() {
	const section = mmCartSectionElement();
	if (!section) return;

	const restrictionEl = section.querySelector('[data-solo-restriction]');
	const isSolo = !!restrictionEl && restrictionEl.dataset.soloRestriction === 'true';
	const isChLimit = !!restrictionEl && !!restrictionEl.dataset.chLimit;
	const isRestricted = isSolo || isChLimit;

	const messageEl = section.querySelector('#cart-restriction-message');
	const checkoutBtn = section.querySelector('.mm-dc-cta');
	const dynamicBtns = section.querySelector('.additional-checkout-buttons');

	if (isRestricted) {
		if (messageEl) messageEl.style.display = isSolo ? 'block' : 'none';
		if (checkoutBtn) {
			checkoutBtn.style.pointerEvents = 'none';
			checkoutBtn.style.opacity = '0.5';
			checkoutBtn.setAttribute('aria-disabled', 'true');
		}
		if (dynamicBtns) dynamicBtns.style.display = 'none';
	} else {
		if (messageEl) messageEl.style.display = 'none';
		if (checkoutBtn) {
			checkoutBtn.style.pointerEvents = '';
			checkoutBtn.style.opacity = '';
			checkoutBtn.removeAttribute('aria-disabled');
		}
		if (dynamicBtns) dynamicBtns.style.display = '';
	}
}

document.addEventListener('cart:updated', mmCheckSoloRestriction);

/*********************************************************************************************
 * Toggle affichage des détails enfants du pack dans le panier
 */
function mmSetupChildrenDetailsToggle() {
	// Délégation d'événement pour survivre aux re-renders de la section panier
	document.addEventListener('click', function(e) {
		const trigger = e.target.closest('.mm-dc-product-children-details-link');
		if (!trigger) return;
		const container = trigger.closest('.mm-dc-product-container');
		if (!container) return;
		const wrapper = container.querySelector('.mm-dc-product-children-wrapper');
		if (!wrapper) return;
		
		// Toggle de la visibilité
		const isHidden = wrapper.classList.toggle('visually-hidden');
		
		// Changement du texte
		const textSee = trigger.getAttribute('data-text-see');
		const textReduce = trigger.getAttribute('data-text-reduce');
		if (textSee && textReduce) {
			trigger.textContent = isHidden ? textSee : textReduce;
		}
	});
}